import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Users, ClipboardList, Calendar, CalendarCheck, Mail, Package, Image, BarChart3, Settings, Plus, Pencil, Trash2, X, Eye, MousePointerClick, TrendingUp, Monitor, Smartphone, Tablet, FileText, Sliders } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminEtude from "@/components/study/AdminEtude";
import DocumentsPanel from "@/pages/DocumentsPanel";
import ChantiersPanel from "@/pages/ChantiersPanel";
import RemindersBar from "@/components/billing/RemindersBar";
import NotificationBell from "@/components/admin/NotificationBell";
import AvailabilityPanel from "@/components/admin/AvailabilityPanel";
import CommunicationsPanel from "@/components/admin/CommunicationsPanel";

const STATUSES = ["nouveau", "a_rappeler", "rdv_prevu", "visite_effectuee", "a_chiffrer", "devis_envoye", "accepte", "refuse", "chantier_en_cours", "termine"];

const EMPTY_REAL = {
  title: "", ville: "", surface: "", duree: "", type: "Rénovation intérieure",
  tags: [], image_before: "", image_after: "", description: "", published: true, order: 0,
};

export default function AdminDashboardPage() {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [realisations, setRealisations] = useState([]);
  const [editingReal, setEditingReal] = useState(null); // null | EMPTY_REAL | existing
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/auth/me").catch(() => { localStorage.removeItem("rama_token"); nav("/admin/login"); });
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const [s, p, pr, a, r, an] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/projects"),
        api.get("/prestations"),
        api.get("/admin/appointments"),
        api.get("/admin/realisations"),
        api.get("/admin/analytics"),
      ]);
      setStats(s.data); setProjects(p.data.items); setPrestations(pr.data.items);
      setAppointments(a.data.items); setRealisations(r.data.items); setAnalytics(an.data);
    } catch (e) { console.error(e); }
  };

  const logout = () => { localStorage.removeItem("rama_token"); nav("/admin/login"); };
  const updateStatus = async (id, status) => {
    await api.put(`/admin/projects/${id}/status`, { status });
    toast.success("Statut mis à jour");
    refresh();
  };

  // Realisations
  const saveReal = async () => {
    const body = { ...editingReal };
    body.tags = typeof body.tags === "string" ? body.tags.split(",").map(t => t.trim()).filter(Boolean) : body.tags;
    body.order = Number(body.order) || 0;
    try {
      if (body.id) await api.put(`/admin/realisations/${body.id}`, body);
      else await api.post("/admin/realisations", body);
      toast.success("Réalisation enregistrée");
      setEditingReal(null); refresh();
    } catch { toast.error("Erreur"); }
  };
  const deleteReal = async (id) => {
    if (!confirm("Supprimer cette réalisation ?")) return;
    await api.delete(`/admin/realisations/${id}`);
    toast.success("Supprimée"); refresh();
  };

  // Password
  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) { toast.error("Les nouveaux mots de passe ne correspondent pas."); return; }
    if (pwd.next.length < 8) { toast.error("Au moins 8 caractères."); return; }
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Mot de passe mis à jour");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const tabs = [
    { v: "dashboard", l: "Dashboard", i: ClipboardList },
    { v: "chantiers", l: "Projets", i: FileText },
    { v: "analytics", l: "Analytics", i: BarChart3 },
    { v: "appointments", l: "Rendez-vous", i: Calendar },
    { v: "disponibilites", l: "Disponibilités", i: CalendarCheck },
    { v: "communications", l: "Communications", i: Mail },
    { v: "etude", l: "Bureau d'étude", i: Sliders },
    { v: "realisations", l: "Réalisations", i: Image },
    { v: "prestations", l: "Catalogue", i: Package },
    { v: "settings", l: "Réglages", i: Settings },
  ];

  return (
    <div className="min-h-screen bg-ivory">
      <header className="bg-anthracite text-ivory sticky top-0 z-40">
        <div className="wrap flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/logo-nom-ivory.png" alt="RAMA DÉCO" className="brand-logo h-8" />
            <span className="font-mono-caption text-white/60 hidden sm:inline">Admin</span>
          </Link>
          <div className="flex items-center gap-2">
          <NotificationBell onOpenProject={() => setTab("chantiers")} />
          <button data-testid="admin-logout" onClick={logout} className="text-sm inline-flex items-center gap-2 text-white/70 hover:text-ivory">
            <LogOut size={16} /> Déconnexion
          </button>
          </div>
        </div>
        <nav className="wrap flex gap-1 overflow-x-auto pb-3">
          {tabs.map(t => (
            <button key={t.v} data-testid={`admin-tab-${t.v}`} onClick={() => { setTab(t.v); window.scrollTo({ top: 0, behavior: "instant" }); }}
              className={`inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-rama-sm text-sm whitespace-nowrap transition ${tab === t.v ? "bg-bordeaux-700 text-ivory" : "text-white/70 hover:text-ivory hover:bg-white/5"}`}>
              <t.i size={14} /> {t.l}
            </button>
          ))}
        </nav>
      </header>

      <div className="wrap py-8">
        {/* ============== DOCUMENTS ============== */}
        {tab === "chantiers" && <ChantiersPanel />}
        {tab === "disponibilites" && <AvailabilityPanel />}
        {tab === "communications" && <CommunicationsPanel />}
        {tab === "etude" && <div className="bg-anthracite rounded-rama-md p-5 md:p-8"><AdminEtude /></div>}

        {/* ============== DASHBOARD ============== */}
        {tab === "dashboard" && stats && (
          <>
            <h1 className="font-display text-3xl md:text-4xl text-anthracite mb-6">Vue d'ensemble</h1>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { l: "Projets totaux", v: stats.total_projects },
                { l: "Aujourd'hui", v: stats.projects_today },
                { l: "Estimés", v: stats.projects_estimated },
                { l: "Rendez-vous", v: stats.appointments },
              ].map(k => (
                <div key={k.l} className="bg-white p-6 border border-black/10 rounded-rama-md">
                  <div className="font-mono-caption text-ink-soft mb-2">{k.l}</div>
                  <div className="font-display text-4xl text-anthracite">{k.v}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ============== ANALYTICS ============== */}
        {tab === "analytics" && analytics && (
          <>
            <h1 className="font-display text-3xl md:text-4xl text-anthracite mb-6">Fréquentation du site</h1>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { l: "Vues aujourd'hui", v: analytics.events_today, i: Eye },
                { l: "Vues (7 j)", v: analytics.events_7d, i: TrendingUp },
                { l: "Visiteurs uniques (7 j)", v: analytics.unique_visitors_7d, i: Users },
                { l: "Vues totales", v: analytics.total_events, i: MousePointerClick },
              ].map(k => (
                <div key={k.l} className="bg-white p-6 border border-black/10 rounded-rama-md">
                  <k.i size={18} className="text-bordeaux-700 mb-3" />
                  <div className="font-mono-caption text-ink-soft mb-1">{k.l}</div>
                  <div className="font-display text-3xl text-anthracite">{k.v}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-6 border border-black/10 rounded-rama-md">
                <div className="font-mono-caption text-ink-soft mb-4">Vues sur les 7 derniers jours</div>
                <div className="space-y-2">
                  {analytics.daily.length ? analytics.daily.map(d => {
                    const max = Math.max(...analytics.daily.map(x => x.count));
                    return (
                      <div key={d.date} className="flex items-center gap-3 text-sm">
                        <span className="w-24 text-ink-soft text-xs font-mono-caption">{new Date(d.date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</span>
                        <div className="flex-1 h-6 bg-black/5 rounded relative">
                          <div className="absolute inset-y-0 left-0 bg-bordeaux-700 rounded" style={{ width: `${max ? (d.count / max) * 100 : 0}%` }} />
                        </div>
                        <span className="w-16 text-right text-anthracite tabular-nums">{d.count} <span className="text-ink-soft text-xs">({d.unique})</span></span>
                      </div>
                    );
                  }) : <div className="text-ink-soft text-sm">Aucune donnée pour l'instant.</div>}
                </div>
              </div>

              <div className="bg-white p-6 border border-black/10 rounded-rama-md">
                <div className="font-mono-caption text-ink-soft mb-4">Pages les plus vues (7 j)</div>
                <ul className="space-y-2 text-sm">
                  {analytics.top_pages.length ? analytics.top_pages.map(p => (
                    <li key={p.path} className="flex justify-between items-center gap-4 py-1 border-b border-black/5">
                      <span className="text-anthracite truncate">{p.path || "/"}</span>
                      <span className="font-mono-caption text-bordeaux-700">{p.count}</span>
                    </li>
                  )) : <div className="text-ink-soft">—</div>}
                </ul>

                <div className="mt-6 pt-6 border-t border-black/5">
                  <div className="font-mono-caption text-ink-soft mb-3">Appareils (7 j)</div>
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-2 text-anthracite"><Monitor size={14} /> {analytics.devices.desktop || 0}</div>
                    <div className="flex items-center gap-2 text-anthracite"><Smartphone size={14} /> {analytics.devices.mobile || 0}</div>
                    <div className="flex items-center gap-2 text-anthracite"><Tablet size={14} /> {analytics.devices.tablet || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============== PROJECTS ============== */}
        {tab === "projects" && (
          <>
            <h1 className="font-display text-3xl md:text-4xl text-anthracite mb-6">Projets</h1>
            <div className="bg-white border border-black/10 rounded-rama-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/5 text-ink-soft font-mono-caption">
                  <tr><th className="p-4 text-left">Ref</th><th className="text-left">Type</th><th className="text-left">Prestations</th><th className="text-left">Estimation</th><th className="text-left">Statut</th><th className="text-left">Contact</th></tr>
                </thead>
                <tbody>
                  {projects.map(p => (
                    <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-t border-black/5">
                      <td className="p-4 font-mono text-bordeaux-700 tabular-nums">{p.ref}</td>
                      <td>{p.type_projet || "—"}</td>
                      <td>{p.prestations?.length || 0}</td>
                      <td>{p.estimation ? `${p.estimation.total_min.toLocaleString("fr-FR")} — ${p.estimation.total_max.toLocaleString("fr-FR")} €` : "—"}</td>
                      <td>
                        <select value={p.status || "nouveau"} onChange={(e) => updateStatus(p.id, e.target.value)} className="border border-black/10 rounded px-2 py-1 bg-white">
                          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="text-xs">{p.contact?.nom} {p.contact?.telephone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ============== APPOINTMENTS ============== */}
        {tab === "appointments" && (
          <>
            <h1 className="font-display text-3xl md:text-4xl text-anthracite mb-6">Rendez-vous</h1>
            <div className="bg-white border border-black/10 rounded-rama-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/5 text-ink-soft font-mono-caption"><tr><th className="p-4 text-left">Date</th><th className="text-left">Créneau</th><th className="text-left">Type</th><th className="text-left">Nom</th><th className="text-left">Contact</th><th className="text-left">Adresse</th></tr></thead>
                <tbody>
                  {appointments.map(a => (
                    <tr key={a.id} className="border-t border-black/5">
                      <td className="p-4">{new Date(a.date).toLocaleDateString("fr-FR")}</td>
                      <td>{a.creneau}</td><td>{a.type_rdv}</td>
                      <td>{a.nom} {a.prenom}</td><td className="text-xs">{a.telephone} · {a.email}</td><td>{a.adresse}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ============== REALISATIONS ============== */}
        {tab === "realisations" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display text-3xl md:text-4xl text-anthracite">Réalisations</h1>
              <button data-testid="add-realisation" onClick={() => setEditingReal({ ...EMPTY_REAL })} className="btn-primary text-sm"><Plus size={16} /> Ajouter</button>
            </div>
            <p className="text-sm text-ink-soft mb-6">Les réalisations sont affichées publiquement sur la page <a href="/realisations" target="_blank" rel="noreferrer" className="text-bordeaux-700 underline">Réalisations</a> avec un slider avant/après. Utilisez des URLs d'images (formats standards, minimum 1200 px de large).</p>
            <div className="grid md:grid-cols-2 gap-4">
              {realisations.map(r => (
                <div key={r.id} data-testid={`realisation-${r.id}`} className="bg-white border border-black/10 rounded-rama-md overflow-hidden">
                  <div className="grid grid-cols-2 aspect-[16/6]">
                    <img src={r.image_before || "https://via.placeholder.com/600x400?text=Avant"} alt="Avant" className="w-full h-full object-cover" />
                    <img src={r.image_after || "https://via.placeholder.com/600x400?text=Apres"} alt="Après" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-display text-lg text-anthracite">{r.title}</div>
                        <div className="text-xs text-ink-soft mt-1">{r.ville} · {r.surface} · {r.duree}</div>
                        <div className="text-xs text-bordeaux-700 mt-1">{r.type}</div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditingReal({ ...r, tags: (r.tags || []).join(", ") })} data-testid={`edit-realisation-${r.id}`} className="p-2 hover:bg-black/5 rounded" title="Modifier"><Pencil size={14} /></button>
                        <button onClick={() => deleteReal(r.id)} data-testid={`delete-realisation-${r.id}`} className="p-2 hover:bg-black/5 rounded text-bordeaux-700" title="Supprimer"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    {!r.published && <div className="mt-2 inline-block text-[0.65rem] bg-black/10 text-anthracite px-2 py-0.5 rounded">Brouillon</div>}
                  </div>
                </div>
              ))}
              {realisations.length === 0 && (
                <div className="col-span-2 p-10 border border-dashed border-black/15 rounded-rama-md text-center text-ink-soft">
                  Aucune réalisation. Cliquez sur « Ajouter » pour en créer une.
                </div>
              )}
            </div>
          </>
        )}

        {/* ============== PRESTATIONS ============== */}
        {tab === "prestations" && (
          <>
            <h1 className="font-display text-3xl md:text-4xl text-anthracite mb-6">Catalogue</h1>
            <div className="bg-white border border-black/10 rounded-rama-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/5 text-ink-soft font-mono-caption"><tr><th className="p-4 text-left">Nom</th><th className="text-left">Famille</th><th className="text-left">Unité</th><th className="text-left">Min</th><th className="text-left">Réf</th><th className="text-left">Max</th></tr></thead>
                <tbody>
                  {prestations.map(p => (
                    <tr key={p.id} className="border-t border-black/5">
                      <td className="p-4">{p.nom}</td><td>{p.famille}</td><td>{p.unite}</td>
                      <td>{p.prix_min} €</td><td>{p.prix_ref} €</td><td>{p.prix_max} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ============== SETTINGS ============== */}
        {tab === "settings" && (
          <>
            <h1 className="font-display text-3xl md:text-4xl text-anthracite mb-6">Réglages</h1>
            <div className="bg-white border border-black/10 rounded-rama-md p-6 md:p-8 max-w-xl">
              <div className="font-mono-caption text-ink-soft mb-2">Sécurité</div>
              <h2 className="font-display text-2xl text-anthracite mb-6">Changer le mot de passe</h2>
              <form onSubmit={changePwd} className="space-y-5">
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">Mot de passe actuel</label>
                  <input required type="password" data-testid="pwd-current" value={pwd.current} onChange={e => setPwd({...pwd, current: e.target.value})} className="w-full py-3 px-4 bg-ivory border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none" />
                </div>
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">Nouveau mot de passe</label>
                  <input required type="password" data-testid="pwd-new" value={pwd.next} onChange={e => setPwd({...pwd, next: e.target.value})} className="w-full py-3 px-4 bg-ivory border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none" />
                  <div className="text-xs text-ink-soft mt-1">8 caractères minimum.</div>
                </div>
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">Confirmer</label>
                  <input required type="password" data-testid="pwd-confirm" value={pwd.confirm} onChange={e => setPwd({...pwd, confirm: e.target.value})} className="w-full py-3 px-4 bg-ivory border border-black/10 rounded-rama-sm focus:border-bordeaux-700 outline-none" />
                </div>
                <button data-testid="pwd-submit" className="btn-primary">Enregistrer</button>
              </form>
            </div>
            <div className="mt-10"><RemindersBar /></div>
            <div className="mt-6" data-testid="settings-docs">
              <DocumentsPanel onlyTabs={["clients", "parametres"]} title="Clients & paramètres des documents" />
            </div>
          </>
        )}
      </div>

      {/* MODAL - Realisation editor */}
      {editingReal && (
        <div className="fixed inset-0 z-50 bg-anthracite/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-rama-md my-8 overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <h3 className="font-display text-xl text-anthracite">{editingReal.id ? "Modifier la réalisation" : "Nouvelle réalisation"}</h3>
              <button onClick={() => setEditingReal(null)} className="p-2 hover:bg-black/5 rounded"><X size={18} /></button>
            </div>
            <div className="p-5 md:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-xs font-mono-caption text-ink-soft block mb-2">Titre *</label>
                <input required data-testid="real-title" value={editingReal.title} onChange={e => setEditingReal({...editingReal, title: e.target.value})} className="w-full py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" placeholder="Ex : Appartement Haussmannien" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <input data-testid="real-ville" value={editingReal.ville} onChange={e => setEditingReal({...editingReal, ville: e.target.value})} placeholder="Ville" className="py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" />
                <input data-testid="real-surface" value={editingReal.surface} onChange={e => setEditingReal({...editingReal, surface: e.target.value})} placeholder="Surface (ex: 72 m²)" className="py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" />
                <input data-testid="real-duree" value={editingReal.duree} onChange={e => setEditingReal({...editingReal, duree: e.target.value})} placeholder="Durée (ex: 8 semaines)" className="py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" />
              </div>
              <div>
                <label className="text-xs font-mono-caption text-ink-soft block mb-2">Type</label>
                <input data-testid="real-type" value={editingReal.type} onChange={e => setEditingReal({...editingReal, type: e.target.value})} placeholder="Rénovation intérieure, Salle de bains, Cuisine, Peinture, Second œuvre neuf…" className="w-full py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" />
              </div>
              <div>
                <label className="text-xs font-mono-caption text-ink-soft block mb-2">Tags (séparés par virgule)</label>
                <input data-testid="real-tags" value={typeof editingReal.tags === "string" ? editingReal.tags : (editingReal.tags || []).join(", ")} onChange={e => setEditingReal({...editingReal, tags: e.target.value})} placeholder="Rénovation intérieure, Appartement" className="w-full py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">URL image AVANT *</label>
                  <input required data-testid="real-image-before" value={editingReal.image_before} onChange={e => setEditingReal({...editingReal, image_before: e.target.value})} placeholder="https://…" className="w-full py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" />
                  {editingReal.image_before && <img src={editingReal.image_before} alt="" className="mt-2 aspect-video object-cover rounded-rama-sm" onError={(e)=> e.target.style.display='none'} />}
                </div>
                <div>
                  <label className="text-xs font-mono-caption text-ink-soft block mb-2">URL image APRÈS *</label>
                  <input required data-testid="real-image-after" value={editingReal.image_after} onChange={e => setEditingReal({...editingReal, image_after: e.target.value})} placeholder="https://…" className="w-full py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" />
                  {editingReal.image_after && <img src={editingReal.image_after} alt="" className="mt-2 aspect-video object-cover rounded-rama-sm" onError={(e)=> e.target.style.display='none'} />}
                </div>
              </div>
              <div>
                <label className="text-xs font-mono-caption text-ink-soft block mb-2">Description</label>
                <textarea rows={3} data-testid="real-description" value={editingReal.description} onChange={e => setEditingReal({...editingReal, description: e.target.value})} className="w-full py-2.5 px-3 border border-black/10 rounded-rama-sm outline-none focus:border-bordeaux-700" placeholder="Le besoin, les travaux, l'approche RAMA DÉCO…" />
              </div>
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="real-published" checked={editingReal.published} onChange={e => setEditingReal({...editingReal, published: e.target.checked})} /> Publier
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  Ordre <input type="number" data-testid="real-order" value={editingReal.order} onChange={e => setEditingReal({...editingReal, order: e.target.value})} className="w-16 py-1 px-2 border border-black/10 rounded" />
                </label>
              </div>
            </div>
            <div className="p-5 border-t border-black/10 flex justify-end gap-3">
              <button onClick={() => setEditingReal(null)} className="btn-ghost">Annuler</button>
              <button data-testid="save-realisation" onClick={saveReal} className="btn-primary">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
